import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import PinDropIcon from '@mui/icons-material/PinDrop';

import { DASHBOARD_SCOPE, DashboardT } from 'business/definitions';

import { capitalize } from 'helpers/helpers';
import {
    splitDashboardsByScope,
    getIconFromScope,
} from 'helpers/dashboardHelpers';
import { sortDashboardsbyTitle } from './helpers';
import './_dashboardList.scss';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';

type Props = {
    dashboards: DashboardT[];
    homeDashboardSlug: string;
    dashboardItemRender: (arg1: DashboardT) => React.ReactNode;
};

const orderedDashboardScopes = [
    DASHBOARD_SCOPE.PRIVATE,
    DASHBOARD_SCOPE.SHARED,
    DASHBOARD_SCOPE.RESTRICTED,
    DASHBOARD_SCOPE.PUBLIC,
    DASHBOARD_SCOPE.TEMPLATE,
];

function DashboardList({
    dashboards,
    homeDashboardSlug,
    dashboardItemRender,
}: Props): React.ReactElement {
    const { t } = useTranslation('components');
    const homeSlug = homeDashboardSlug;

    const currentDashboard = useGetDashboardFromSlugOrMetadata();
    const sortedDashboards = sortDashboardsbyTitle(dashboards);
    const homeDashboard = sortedDashboards.find((d) => d.slug === homeSlug);
    const otherDashboards = sortedDashboards.filter(
        (d) =>
            d.slug !== homeDashboardSlug && d.slug !== currentDashboard?.slug,
    );

    const scopedDashboards = splitDashboardsByScope(otherDashboards);

    return (
        <div className="dashboards-list">
            {!!homeDashboard && dashboardItemRender(homeDashboard)}

            {/* Display the current dashboard separately */}
            {!!currentDashboard && (
                <div key="current">
                    <div className="dashboard-list-title">
                        <PinDropIcon />
                        <Typography variant="body1">
                            {capitalize(t('dashboards.currentDashboard'))}
                        </Typography>
                    </div>
                    {dashboardItemRender(currentDashboard)}
                </div>
            )}

            {orderedDashboardScopes
                .filter((scope) => scopedDashboards[scope])
                .map((scope) => (
                    <div key={scope}>
                        <div className="dashboard-list-title">
                            {getIconFromScope(scope)}
                            <Typography variant="body1">
                                {capitalize(t(`dashboards.${scope}`))}
                            </Typography>
                        </div>
                        {scopedDashboards[scope].map((dashboard) =>
                            dashboardItemRender(dashboard),
                        )}
                    </div>
                ))}
        </div>
    );
}
export default DashboardList;
